import {
  Component, Input, Output, EventEmitter, OnInit, OnDestroy,
  ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, switchMap, of, Subscription } from 'rxjs';
import { LanguageService } from '../../../../services/i18n/language';
import {
  CustomsCalculation, CustomsProduct, InvoiceData, InvoiceItem,
  UpdateInvoiceRequest, UpdateInvoiceItemRequest, TnvedSearchResult
} from '../../../../services/api/customs-calculations';
import { CustomsCalculationsService } from '../../../../services/api/customs-calculations';
import { FileService } from '../../../../services/api/file';
import { DictionaryService, IncotermDto } from '../../../../services/api/dictionary';

/** Реквизиты инвойса, которые пользователь может исправить (блоки 1 и 2). */
export interface InvoiceHead {
  invoiceNumber: string;
  invoiceDate: string;
  sellerName: string;
  sellerAddress: string;
  buyerName: string;
  buyerAddress: string;
  paymentTerms: string;
}

const HEAD_FIELDS: (keyof InvoiceHead)[] = [
  'invoiceNumber', 'invoiceDate', 'sellerName', 'sellerAddress', 'buyerName', 'buyerAddress', 'paymentTerms'
];

export interface InvoiceRow {
  product: CustomsProduct;
  invoiceItem?: InvoiceItem;
  editName: string;
  editUnit: string;
  editQty: number | null;
  editUnitPrice: number | null;
  editCost: number | null;
  editCurrency: string;
  editTnved: string;
  /** Раскрыта строка «ИИ определил как…» */
  aiOpen: boolean;
  /** Длинное наименование раскрыто целиком */
  nameOpen: boolean;
  tnvedSuggestions: TnvedSearchResult[];
  showSuggestions: boolean;
}

@Component({
  selector: 'app-invoice-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './invoice-editor.html',
  styleUrl: './invoice-editor.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvoiceEditorComponent implements OnInit, OnDestroy {
  @Input() calculation!: CustomsCalculation;
  /** true для брокера — Incoterms в этом блоке только для просмотра. */
  @Input() incotermReadonly = false;
  /** Только просмотр (админ смотрит расчёт клиента): поля закрыты, сохранения нет. */
  @Input() viewOnly = false;
  @Output() calculationUpdated = new EventEmitter<CustomsCalculation>();
  @Output() incotermChange = new EventEmitter<string>();

  rows: InvoiceRow[] = [];
  saving = false;
  saved = false;
  globalError: string | null = null;
  incoterms: IncotermDto[] = [];

  /** Реквизиты в форме и их исходные значения — чтобы понять, что меняли. */
  head: InvoiceHead = this.emptyHead();
  private originalHead: InvoiceHead = this.emptyHead();
  editIncoterm = '';
  /** Блок 2 (продавец, покупатель, условия) открыт. При ручном вводе без реквизитов — свёрнут. */
  termsOpen = true;
  /** Блок 3 (позиции) открыт. */
  itemsOpen = true;
  /** Сервер вернул реквизиты / позиции без наших правок — значит, пока их не сохраняет. */
  headerNotSaved = false;
  itemsNotSaved = false;

  get notSaved(): boolean {
    return this.headerNotSaved || this.itemsNotSaved;
  }

  invoiceText = 'Инвойс';
  fromDateText = 'от';
  paymentTermsText = 'Условия оплаты:';
  countryOfOriginText = 'Страна происхождения:';
  totalText = 'Итого:';
  sellerText = 'Продавец';
  buyerText = 'Покупатель';
  taxIdText = 'ИНН/VAT:';
  invoiceCostLabelText = 'Инвойсная стоимость';
  dateText = 'Дата';
  paymentTermsLabelText = 'Условия оплаты';
  addressPlaceholderText = 'Адрес, телефон';
  saveText = 'Сохранить';
  savingText = 'Сохраняем…';
  savedText = '✓ Сохранено';
  headerNotSavedText = 'Суммы и коды ТН ВЭД сохранены. Остальные исправления (реквизиты инвойса, наименования, количество, ед. изм., цена) пока не сохраняются на сервере — они вернутся после обновления страницы.';
  incotermsLabelText = 'Условия поставки (Incoterms)';
  incotermNotSpecifiedText = 'Не указаны';
  incotermFromInvoiceHintText = 'Определено автоматически из инвойса';
  itemsTitleText = 'Позиции инвойса';
  aiNoticeTitleText = 'Проверьте данные инвойса.';
  itemsHintText = 'Их распознал искусственный интеллект — он может ошибаться. Если видите неточность или знаете точный код ТН ВЭД, исправьте данные и нажмите «Сохранить»: так мы точнее рассчитаем таможенные платежи.';
  columnUnitText = 'Ед. изм.';
  termsToggleText = 'Продавец, покупатель и условия';
  expandText = 'Развернуть';
  invoiceTotalText = 'Итого по инвойсу';
  collapseText = 'Свернуть';
  descriptionToggleText = 'Описание товара';
  columnNumText = '№';
  columnNameText = 'Наименование';
  columnQtyText = 'Кол-во';
  aiIdentifiedAsText = 'ИИ определил как';
  columnPriceText = 'Цена ед.';
  columnTotalText = 'Сумма';
  columnTnvedText = 'Код ТНВЭД';
  tnvedPlaceholderText = 'Введите код...';
  recalculateText = 'Пересчитать';
  recalculatingText = 'Пересчёт...';
  recalculatedText = '✓ Пересчитано';
  cancelEditingText = 'Отменить';
  errorRecalculateText = 'Не удалось пересчитать инвойс. Попробуйте ещё раз.';

  // Subject для поиска ТНВЭД (один на компонент, ключ = индекс строки)
  private tnvedSearch$: { [idx: number]: Subject<string> } = {};
  private subs = new Subscription();

  constructor(
    private languageService: LanguageService,
    private service: CustomsCalculationsService,
    private fileService: FileService,
    private dictionaryService: DictionaryService,
    private cdr: ChangeDetectorRef
  ) {}

  get invoiceData(): InvoiceData | undefined {
    return this.calculation?.invoiceData;
  }

  /** Есть что показывать — PDF-инвойс ИЛИ товары, введённые вручную при расчёте. */
  get hasInvoice(): boolean {
    return !!this.invoiceData || !!this.calculation?.invoiceFile || (this.calculation?.products?.length ?? 0) > 0;
  }

  /** Поля блоков 1–2 только для просмотра (брокер, поставщик). */
  /** Товары введены вручную — нет ни PDF, ни распознанных данных инвойса. */
  get isManual(): boolean {
    return !this.calculation?.invoiceFile && !this.calculation?.invoiceData;
  }

  /** Блок 2 сворачивается всегда (и для PDF); свёрнут ли по умолчанию — решает buildHead. */
  get termsCollapsible(): boolean {
    return true;
  }

  get readonly(): boolean {
    return this.incotermReadonly || this.viewOnly;
  }

  get isHeadDirty(): boolean {
    return HEAD_FIELDS.some(k => this.head[k].trim() !== this.originalHead[k]);
  }

  get isIncotermDirty(): boolean {
    return this.editIncoterm !== (this.calculation?.incotermCode || '');
  }

  get isRowsDirty(): boolean {
    return this.rows.some(r =>
      r.editCost !== (r.product.productCost ?? null)
      || r.editCurrency !== (r.product.currencyCode ?? 'USD')
      || (r.editTnved || '') !== (r.product.tnvedCodeForBroker ?? '')
      || this.rowExtraChanges(r) !== null
    );
  }

  /** Наименование, ед. изм., количество, цена — что из этого пользователь изменил (null — ничего). */
  private rowExtraChanges(r: InvoiceRow): Partial<UpdateInvoiceItemRequest> | null {
    const changes: Partial<UpdateInvoiceItemRequest> = {};
    if (r.editName.trim() !== (r.product.nameOriginal ?? '').trim()) changes.nameOriginal = r.editName.trim();
    if (r.editUnit.trim() !== (r.invoiceItem?.unit ?? '').trim()) changes.unit = r.editUnit.trim();
    if (r.editQty !== (r.invoiceItem?.quantity ?? null) && r.editQty !== null) changes.quantity = r.editQty;
    if (r.editUnitPrice !== (r.invoiceItem?.unitPrice ?? null) && r.editUnitPrice !== null) changes.unitPrice = r.editUnitPrice;
    return Object.keys(changes).length ? changes : null;
  }

  /** Наименование не помещается в одну строку — показываем свёрнутым в строку. */
  isLongName(row: InvoiceRow): boolean {
    return row.editName.length > 28 || row.editName.includes('\n');
  }

  /** Раскрыть наименование и сразу поставить курсор — чтобы можно было править. */
  openName(row: InvoiceRow, index: number): void {
    row.nameOpen = true;
    this.cdr.markForCheck();
    setTimeout(() => (document.getElementById('inv-name-' + index) as HTMLTextAreaElement | null)?.focus(), 0);
  }

  /** Количество × цена → сумма позиции (если оба известны). */
  onQtyOrPriceChange(row: InvoiceRow): void {
    if (row.editQty != null && row.editUnitPrice != null) {
      row.editCost = Math.round(row.editQty * row.editUnitPrice * 100) / 100;
    }
  }

  get isDirty(): boolean {
    return this.isHeadDirty || this.isIncotermDirty || this.isRowsDirty;
  }

  private emptyHead(): InvoiceHead {
    return { invoiceNumber: '', invoiceDate: '', sellerName: '', sellerAddress: '', buyerName: '', buyerAddress: '', paymentTerms: '' };
  }

  /** Реквизиты из инвойса (PDF) — или пустые, тогда в полях прочерк.
   *  Телефон продавца/покупателя показываем в одном поле с адресом: «адрес, телефон». */
  private buildHead(): void {
    const d = this.invoiceData;
    const head = this.emptyHead();
    for (const k of HEAD_FIELDS) head[k] = ((d?.[k] as string | undefined) ?? '').trim();
    head.sellerAddress = this.joinAddressPhone(d?.sellerAddress, d?.sellerPhone);
    head.buyerAddress = this.joinAddressPhone(d?.buyerAddress, d?.buyerPhone);
    this.head = { ...head };
    this.originalHead = head;
    this.editIncoterm = this.calculation?.incotermCode || '';
    // Ручной ввод: открыт, только если какие-то реквизиты уже есть
    const hasTerms = ['sellerName', 'sellerAddress', 'buyerName', 'buyerAddress', 'paymentTerms']
      .some(k => !!this.head[k as keyof InvoiceHead]);
    this.termsOpen = !this.isManual || hasTerms;
  }

  private joinAddressPhone(address?: string, phone?: string): string {
    const a = (address ?? '').trim();
    const p = (phone ?? '').trim();
    // Телефон уже записан в адресе (после сохранения адрес приходит вместе с ним) — не дублируем
    return p && !a.includes(p) ? [a, p].filter(Boolean).join(', ') : a;
  }

  getIncotermLabel(incoterm: IncotermDto): string {
    return `${incoterm.code} — ${incoterm.name || incoterm.nameRu}`;
  }

  downloadInvoice(event?: Event): void {
    event?.preventDefault();
    const file = this.calculation?.invoiceFile;
    if (!file?.uid) return;
    this.fileService.downloadFile(file.uid, file.fileName);
  }

  formatFileSize(bytes: number | undefined): string {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  ngOnInit(): void {
    this.updateTranslations();
    this.subs.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.dictionaryService.getIncoterms().subscribe({
        next: (incoterms) => { this.incoterms = incoterms; this.cdr.markForCheck(); },
        error: (err) => console.error('Error loading incoterms:', err)
      })
    );
    this.buildRows();
    this.buildHead();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private updateTranslations(): void {
    this.invoiceText = this.t('invoiceEditor.invoice', 'Инвойс');
    this.fromDateText = this.t('invoiceEditor.fromDate', 'от');
    this.paymentTermsText = this.t('invoiceEditor.paymentTerms', 'Условия оплаты:');
    this.countryOfOriginText = this.t('invoiceEditor.countryOfOrigin', 'Страна происхождения:');
    this.totalText = this.t('invoiceEditor.total', 'Итого:');
    this.sellerText = this.t('invoiceEditor.seller', 'Продавец');
    this.buyerText = this.t('invoiceEditor.buyer', 'Покупатель');
    this.taxIdText = this.t('invoiceEditor.taxId', 'ИНН/VAT:');
    this.invoiceCostLabelText = this.t('calculationSummary.invoiceCostLabel', 'Инвойсная стоимость');
    this.dateText = this.t('invoiceEditor.date', 'Дата');
    this.paymentTermsLabelText = this.t('invoiceEditor.paymentTermsLabel', 'Условия оплаты');
    this.addressPlaceholderText = this.t('invoiceEditor.addressPlaceholder', 'Адрес, телефон');
    this.saveText = this.t('invoiceEditor.save', 'Сохранить');
    this.savingText = this.t('invoiceEditor.saving', 'Сохраняем…');
    this.savedText = this.t('invoiceEditor.saved', '✓ Сохранено');
    this.headerNotSavedText = this.t('invoiceEditor.headerNotSaved', this.headerNotSavedText);
    this.incotermsLabelText = this.t('calculationSummary.incotermsLabel', 'Условия поставки (Incoterms)');
    this.incotermNotSpecifiedText = this.t('calculationSummary.incotermNotSpecified', 'Не указаны');
    this.incotermFromInvoiceHintText = this.t('invoiceEditor.incotermFromInvoiceHint', 'Определено автоматически из инвойса');
    this.itemsTitleText = this.t('invoiceEditor.itemsTitle', 'Позиции инвойса');
    this.aiNoticeTitleText = this.t('invoiceEditor.aiNoticeTitle', this.aiNoticeTitleText);
    this.itemsHintText = this.t('invoiceEditor.itemsHint', this.itemsHintText);
    this.columnUnitText = this.t('invoiceEditor.columnUnit', this.columnUnitText);
    this.termsToggleText = this.t('invoiceEditor.termsToggle', this.termsToggleText);
    this.expandText = this.t('invoiceEditor.expand', this.expandText);
    this.invoiceTotalText = this.t('invoiceEditor.invoiceTotal', this.invoiceTotalText);
    this.collapseText = this.t('invoiceEditor.collapse', this.collapseText);
    this.descriptionToggleText = this.t('invoiceEditor.descriptionToggle', this.descriptionToggleText);
    this.columnNumText = this.t('invoiceEditor.columnNum', '№');
    this.columnNameText = this.t('invoiceEditor.columnName', 'Наименование');
    this.columnQtyText = this.t('invoiceEditor.columnQty', 'Кол-во');
    this.aiIdentifiedAsText = this.t('invoiceEditor.aiIdentifiedAs', 'ИИ определил как');
    this.columnPriceText = this.t('invoiceEditor.columnPrice', 'Цена ед.');
    this.columnTotalText = this.t('invoiceEditor.columnTotal', 'Сумма');
    this.columnTnvedText = this.t('invoiceEditor.columnTnved', 'Код ТНВЭД');
    this.tnvedPlaceholderText = this.t('invoiceEditor.tnvedPlaceholder', 'Введите код...');
    this.recalculateText = this.t('invoiceEditor.recalculate', 'Пересчитать');
    this.recalculatingText = this.t('invoiceEditor.recalculating', 'Пересчёт...');
    this.recalculatedText = this.t('invoiceEditor.recalculated', '✓ Пересчитано');
    this.cancelEditingText = this.t('invoiceEditor.cancel', 'Отменить');
    this.errorRecalculateText = this.t('invoiceEditor.errorRecalculate', 'Не удалось пересчитать инвойс. Попробуйте ещё раз.');
  }

  private buildRows(): void {
    if (!this.calculation?.products) { this.rows = []; return; }

    const products = [...this.calculation.products].sort(
      (a, b) => ((a as any).sortOrder ?? 0) - ((b as any).sortOrder ?? 0)
    );

    const items = this.invoiceData?.items ?? [];

    this.rows = products.map(p => {
      const sortOrder = (p as any).sortOrder ?? 0;
      const invoiceItem = items.find(i => (i.lineNumber ?? 1) - 1 === sortOrder);
      return {
        product: p,
        invoiceItem,
        editName: p.nameOriginal ?? '',
        editUnit: invoiceItem?.unit ?? '',
        editQty: invoiceItem?.quantity ?? null,
        editUnitPrice: invoiceItem?.unitPrice ?? null,
        editCost: p.productCost ?? null,
        editCurrency: p.currencyCode ?? 'USD',
        editTnved: p.tnvedCodeForBroker ?? '',
        aiOpen: false,
        nameOpen: false,
        tnvedSuggestions: [],
        showSuggestions: false
      } as InvoiceRow;
    });
  }

  onTnvedInput(row: InvoiceRow, idx: number, value: string): void {
    row.editTnved = value;
    row.showSuggestions = false;
    row.tnvedSuggestions = [];

    const digits = value.replace(/\D/g, '');
    if (digits.length < 2) return;

    if (!this.tnvedSearch$[idx]) {
      this.tnvedSearch$[idx] = new Subject<string>();
      this.subs.add(
        this.tnvedSearch$[idx].pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap(prefix => prefix.length >= 2 ? this.service.searchTnved(prefix) : of([]))
        ).subscribe(results => {
          row.tnvedSuggestions = results;
          row.showSuggestions = results.length > 0;
          this.cdr.markForCheck();
        })
      );
    }
    this.tnvedSearch$[idx].next(digits);
  }

  selectTnved(row: InvoiceRow, result: TnvedSearchResult): void {
    row.editTnved = result.code;
    row.showSuggestions = false;
    row.tnvedSuggestions = [];
    this.cdr.markForCheck();
  }

  hideSuggestions(row: InvoiceRow): void {
    setTimeout(() => {
      row.showSuggestions = false;
      this.cdr.markForCheck();
    }, 200);
  }

  cancelEditing(): void {
    if (this.saving) return;
    this.globalError = null;
    this.headerNotSaved = false;
    this.itemsNotSaved = false;
    this.saved = false;
    this.buildRows();
    this.buildHead();
    this.cdr.markForCheck();
  }

  save(): void {
    if (this.viewOnly || this.saving || this.rows.length === 0) return;

    const sentExtras = new Map<number, Partial<UpdateInvoiceItemRequest>>();
    const items = this.rows.map(row => {
      const item: UpdateInvoiceItemRequest = { productId: row.product.id };
      const extras = this.rowExtraChanges(row);
      if (extras) {
        Object.assign(item, extras);
        sentExtras.set(row.product.id, extras);
      }
      if (row.editCost !== null) {
        item.productCost = row.editCost;
        item.currencyCode = row.editCurrency;
      }
      const tnved = (row.editTnved || '').replace(/\D/g, '');
      if (tnved) {
        item.tnvedCode = tnved;
      }
      return item;
    });

    this.saving = true;
    this.saved = false;
    this.globalError = null;
    this.cdr.markForCheck();

    const req: UpdateInvoiceRequest = { items };
    const headChanged = this.isHeadDirty;
    const sentHead: Partial<InvoiceHead> = {};
    if (headChanged) {
      for (const k of HEAD_FIELDS) {
        const value = this.head[k].trim();
        if (value !== this.originalHead[k]) sentHead[k] = value;
      }
      Object.assign(req, sentHead);
    }
    const incotermChanged = this.isIncotermDirty;
    const newIncoterm = this.editIncoterm;
    this.headerNotSaved = false;
    this.itemsNotSaved = false;
    this.subs.add(
      this.service.updateInvoiceItems(this.calculation.id, req).subscribe({
        next: updated => {
          this.saving = false;
          this.saved = true;
          this.calculation = updated;
          // Сервер не вернул наши реквизиты — значит, пока их не сохраняет; честно говорим об этом
          this.headerNotSaved = headChanged && Object.entries(sentHead)
            .some(([k, v]) => ((updated.invoiceData?.[k as keyof InvoiceHead] as string | undefined) ?? '').trim() !== v);
          this.buildRows();
          // То же для позиций: наименование / ед. изм. / количество / цена вернулись без наших правок
          this.itemsNotSaved = [...sentExtras.entries()].some(([productId, sent]) => {
            const row = this.rows.find(r => r.product.id === productId);
            if (!row) return false;
            const now = this.rowExtraValues(row);
            return Object.entries(sent).some(([k, v]) => (now as any)[k] !== v);
          });
          this.buildHead();
          this.calculationUpdated.emit(updated);
          if (incotermChanged) {
            // Условия поставки сохраняет страница (данные о поставке) — до ответа держим выбранное значение
            this.editIncoterm = newIncoterm;
            this.incotermChange.emit(newIncoterm);
          }
          this.cdr.markForCheck();
          setTimeout(() => { this.saved = false; this.cdr.markForCheck(); }, 2500);
        },
        error: () => {
          this.saving = false;
          this.globalError = this.errorRecalculateText;
          this.cdr.markForCheck();
        }
      })
    );
  }

  /** Значения позиции, пришедшие с сервера, — чтобы сверить с отправленными. */
  private rowExtraValues(r: InvoiceRow): Partial<UpdateInvoiceItemRequest> {
    return {
      nameOriginal: (r.product.nameOriginal ?? '').trim(),
      unit: (r.invoiceItem?.unit ?? '').trim(),
      quantity: r.invoiceItem?.quantity ?? undefined,
      unitPrice: r.invoiceItem?.unitPrice ?? undefined
    };
  }

  /** Итого по инвойсу: сумма позиций по каждой валюте (обычно одна). */
  get invoiceTotals(): { currency: string; amount: number }[] {
    const sums = new Map<string, number>();
    for (const r of this.rows) {
      if (r.editCost == null) continue;
      sums.set(r.editCurrency, (sums.get(r.editCurrency) ?? 0) + Number(r.editCost));
    }
    return [...sums.entries()].map(([currency, amount]) => ({ currency, amount: Math.round(amount * 100) / 100 }));
  }

  get invoiceTotalsLabel(): string {
    return this.invoiceTotals
      .map(t => `${t.amount.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${t.currency}`)
      .join(' + ');
  }

  /** Тенге с пробелом между тысячами: «4 513 100». */
  formatKzt(value: number | undefined): string {
    return (value ?? 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
  }

  formatCurrency(value: number | undefined, currency: string | undefined): string {
    if (value == null) return '—';
    return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${currency ?? ''}`;
  }
}
